import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth, useTheme } from "../App";
import AudioButton from "../components/AudioButton";
import { 
  Search, 
  Star,
  Filter,
  Moon,
  Sun,
  Home,
  User,
  LogOut,
  BookOpen,
  Heart,
  Sparkles,
  Plus,
  Download,
  Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Favorites() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [filteredFavorites, setFilteredFavorites] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchFavorites();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterFavorites();
  }, [search, selectedCategory, selectedLevel, favorites]);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/favorites`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavorites(data);
        setFilteredFavorites(data);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const filterFavorites = () => {
    let filtered = [...favorites];

    // Search filter
    if (search) {
      filtered = filtered.filter(word =>
        word.baoulé.toLowerCase().includes(search.toLowerCase()) ||
        word.français.toLowerCase().includes(search.toLowerCase()) ||
        (word.phonétique && word.phonétique.toLowerCase().includes(search.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(word => word.catégorie === selectedCategory);
    }

    // Level filter
    if (selectedLevel !== "all") {
      filtered = filtered.filter(word => word.niveau === selectedLevel);
    }

    setFilteredFavorites(filtered);
  };

  const removeFromFavorites = async (wordId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/favorites/${wordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Remove from local state
        setFavorites(prev => prev.filter(w => w.word_id !== wordId));
      }
    } catch (error) {
      console.error("Error removing favorite:", error);
    }
  };

  const createFlashcardSet = async () => {
    if (favorites.length === 0) {
      alert("Aucun favori pour créer un flashcard set");
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/favorites/create-flashcard-set`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Flashcard set créé avec ${data.word_count} mots ! Rendez-vous dans /learn`);
        navigate('/learn');
      }
    } catch (error) {
      console.error("Error creating flashcard set:", error);
      alert("Erreur lors de la création du flashcard set");
    } finally {
      setCreating(false);
    }
  };

  const exportFavorites = () => {
    const data = favorites.map(word => ({
      baoulé: word.baoulé,
      français: word.français,
      phonétique: word.phonétique,
      catégorie: word.catégorie,
      niveau: word.niveau
    }));

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mes-favoris-baoule.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md p-8 text-center">
          <Heart className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="font-heading text-2xl font-bold mb-2">Connectez-vous</h2>
          <p className="text-muted-foreground mb-6">
            Vous devez être connecté pour accéder à vos favoris
          </p>
          <Button onClick={() => navigate('/login')}>
            Se connecter
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="favorites-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-heading font-bold text-xl">B</span>
              </div>
              <span className="font-heading font-bold text-xl hidden sm:block">Apprends Baoulé</span>
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              <Link to="/dashboard" className="p-2 rounded-lg hover:bg-muted transition-colors">
                <Home className="w-5 h-5" />
              </Link>
              <Link to="/profile" className="p-2 rounded-lg hover:bg-muted transition-colors">
                <User className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 animate-slide-up">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                  <h1 className="font-heading text-3xl font-bold">Mes Favoris</h1>
                </div>
                <p className="text-muted-foreground">
                  {favorites.length} {favorites.length > 1 ? 'mots favoris' : 'mot favori'}
                </p>
              </div>
              
              <div className="flex gap-2">
                {favorites.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={exportFavorites}
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Exporter
                    </Button>
                    <Button
                      onClick={createFlashcardSet}
                      disabled={creating}
                      className="gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      {creating ? "Création..." : "Créer Flashcards"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          {favorites.length > 0 && (
            <Card className="border-border/50 mb-8 animate-slide-up stagger-1">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher dans mes favoris..."
                      className="pl-10"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  {/* Category Filter */}
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-full sm:w-48">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les catégories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Level Filter */}
                  <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue placeholder="Niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous niveaux</SelectItem>
                      <SelectItem value="débutant">Débutant</SelectItem>
                      <SelectItem value="intermédiaire">Intermédiaire</SelectItem>
                      <SelectItem value="avancé">Avancé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Words Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-20">
              <Star className="w-20 h-20 mx-auto text-muted-foreground mb-6" />
              <h3 className="font-heading text-2xl font-semibold mb-3">Aucun favori</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Commencez à ajouter des mots à vos favoris depuis le dictionnaire en cliquant sur l'étoile ⭐
              </p>
              <Button onClick={() => navigate('/dictionary')} className="gap-2">
                <BookOpen className="w-4 h-4" />
                Explorer le dictionnaire
              </Button>
            </div>
          ) : filteredFavorites.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Aucun résultat</h3>
              <p className="text-muted-foreground">
                Essayez de modifier vos filtres ou votre recherche
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
              {filteredFavorites.map((word) => (
                <Card 
                  key={word.word_id} 
                  className="word-card border-border/50 hover:border-primary/30 group"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-bold">{word.baoulé}</h3>
                        {word.phonétique && (
                          <p className="font-mono text-sm text-muted-foreground">
                            /{word.phonétique}/
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <AudioButton 
                          text={word.baoulé}
                          phonetic={word.phonétique}
                          size="md"
                        />
                        <button
                          onClick={() => removeFromFavorites(word.word_id)}
                          className="p-2 rounded-full hover:bg-destructive/10 transition-colors group/btn"
                          title="Retirer des favoris"
                        >
                          <Trash2 className="w-4 h-4 text-destructive opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </div>
                    </div>

                    <p className="text-lg mb-3">{word.français}</p>

                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-muted text-xs">
                        {word.catégorie}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        word.niveau === "débutant" 
                          ? "bg-accent/10 text-accent"
                          : word.niveau === "intermédiaire"
                          ? "bg-primary/10 text-primary"
                          : "bg-secondary/10 text-secondary"
                      }`}>
                        {word.niveau}
                      </span>
                    </div>

                    {word.exemple_baoulé && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm italic text-muted-foreground">
                          "{word.exemple_baoulé}"
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {word.exemple_français}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
